
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { User, Bot } from "lucide-react";
import { SEO } from "@/components/SEO";

interface ChatSession {
  session_id: string;
  messages: {
    role: "user" | "assistant";
    content: string;
    created_at: string;
  }[];
  created_at: string;
}

const AdminChatHistory = () => {
  const [chatSessions, setChatSessions] = useState<ChatSession[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchChatSessions = async () => {
      try {
        const { data, error } = await supabase
          .from('chat_messages')
          .select('*')
          .order('created_at', { ascending: true });

        if (error) {
          console.error('Error fetching chat sessions:', error);
          return;
        }

        // Group messages by session_id
        const sessions: { [key: string]: ChatSession } = {};
        data.forEach((message) => {
          if (!message.session_id) return;
          
          if (!sessions[message.session_id]) {
            sessions[message.session_id] = {
              session_id: message.session_id,
              messages: [],
              created_at: message.created_at,
            };
          }
          
          sessions[message.session_id].messages.push({
            role: message.role as "user" | "assistant",
            content: message.content,
            created_at: message.created_at,
          });
        });

        // Convert to array and sort by most recent first
        const sortedSessions = Object.values(sessions).sort(
          (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );

        setChatSessions(sortedSessions);
      } catch (err) {
        console.error('Exception fetching chat sessions:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchChatSessions();
  }, []);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO 
        title="Chat History | Admin Dashboard" 
        description="Monitor and review SpudBot chat conversations"
      />
      
      <div className="container mx-auto py-8 px-4 pt-24">
        <h1 className="text-2xl font-bold mb-6">SpudBot Chat History</h1>
        
        {isLoading ? (
          <div className="text-center py-8">Loading conversations...</div>
        ) : (
          <div className="space-y-6">
            {chatSessions.map((session) => (
              <Card key={session.session_id} className="shadow-sm">
                <CardHeader className="bg-gray-50 border-b">
                  <CardTitle className="text-sm font-medium text-gray-600">
                    Conversation started {formatDate(session.created_at)}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4 space-y-4">
                  {session.messages.map((message, index) => (
                    <div
                      key={index}
                      className={`flex items-start gap-3 ${
                        message.role === "user" ? "flex-row-reverse" : "flex-row"
                      }`}
                    >
                      <div
                        className={`flex h-8 w-8 shrink-0 select-none items-center justify-center rounded-full ${
                          message.role === "user"
                            ? "bg-primary text-primary-foreground"
                            : "bg-[#166534] text-white"
                        }`}
                      >
                        {message.role === "user" ? (
                          <User className="h-5 w-5" />
                        ) : (
                          <Bot className="h-5 w-5" />
                        )}
                      </div>
                      <div
                        className={`flex max-w-[80%] rounded-lg px-4 py-2 text-sm ${
                          message.role === "user"
                            ? "bg-primary text-primary-foreground"
                            : "bg-[#166534] text-white"
                        }`}
                      >
                        {message.content}
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminChatHistory;
